import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Chip,
  Button,
  TextField,
  Modal,
  Divider,
  Grid,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Collapse,
  CircularProgress,
  Pagination,
  Stack,
} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { FaEdit } from 'react-icons/fa';
import config from '../config';

const server_URL = config.SERVER_URL;

const Members = () => {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const itemsPerPage = 100; // Show 100 records per page
  const [customers, setCustomers] = useState([]);
  const [tags, setTags] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [loggedUserId, setLoggedUserId] = useState(null);
  const [customerFilter, setCustomerFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [unsubscribedFilter, setUnsubscribedFilter] = useState('all');
  const [emailSearch, setEmailSearch] = useState('');
  const [duplicateFilter, setDuplicateFilter] = useState('all');
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [profileNote, setProfileNote] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [tagsListOpen, setTagsListOpen] = useState(false);
  const [toastState, setToastState] = useState({ open: false, message: '', severity: 'info' });
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingActivityLog, setLoadingActivityLog] = useState(false);
  const [emailActivityLogs, setEmailActivityLogs] = useState([]);
  const [loadingEmailActivityLog, setLoadingEmailActivityLog] = useState(false);
  const [activeLogTab, setActiveLogTab] = useState('email-log');
  const [migrationOpen, setMigrationOpen] = useState(false);
  const [duplicateRecords, setDuplicateRecords] = useState([]);
  const [migrationData, setMigrationData] = useState({
    email: '',
    customer_id: '',
    first_name: '',
    last_name: '',
    cust1: '',
    cust2: '',
    cust3: '',
    cust4: '',
    cust5: '',
    cust6: '',
    note: ''
  });
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationCustomFields, setMigrationCustomFields] = useState([]);

  const showToast = (message, severity = 'info') => {
    setToastState({ open: true, message, severity });
  };

  useEffect(() => {
    // Get logged user info
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role_id || null);
    setLoggedUserId(userData.id || null);
    fetchMembers(1);
  }, []);

  useEffect(() => {
    if (userRole !== null && loggedUserId !== null) {
      fetchCustomers();
      fetchTags();
    }
  }, [userRole, loggedUserId]);

  useEffect(() => {
    fetchTags();
  }, [customerFilter]);

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when filters change
    fetchMembers(1);
  }, [customerFilter, tagFilter, unsubscribedFilter, emailSearch]);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(server_URL + '/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          method: 'list',
          user_id: loggedUserId,
          role_id: userRole
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCustomers(data.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'list',
        user_id: userData.id || null,
        role_id: userData.role_id || null
      };
      
      // Filter tags by selected customer if customer filter is set
      if (customerFilter !== null && customerFilter !== '') {
        requestBody.customer_id = customerFilter;
      } else if (userData.role_id === 2 && userData.id) {
        // If role is 2 and no customer filter, filter by logged user's customer_id
        requestBody.customer_id = userData.id;
      }
      
      const response = await fetch(server_URL + '/api/tags', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tags');
      }

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setTags(data.data || []);
        // Clear tag filter if selected tag is not in the filtered list
        if (tagFilter !== '' && !data.data.some(tag => tag.id === parseInt(tagFilter))) {
          setTagFilter('');
        }
      } else {
        setTags([]);
        setTagFilter('');
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      setTags([]);
      setTagFilter('');
    }
  };

  const fetchMembers = async (page = 1) => {
    setIsLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'list',
        user_id: userData.id || null,
        role_id: userData.role_id || null,
        page: page,
        per_page: itemsPerPage
      };
      
      // Add filters
      if (customerFilter !== null && customerFilter !== '') {
        requestBody.customer_id = customerFilter;
      }
      
      if (tagFilter !== null && tagFilter !== '') {
        requestBody.tag_id = tagFilter;
      }
      
      if (unsubscribedFilter !== null && unsubscribedFilter !== '' && unsubscribedFilter !== 'all') {
        requestBody.unsubscribed = unsubscribedFilter;
      }
      
      // Add email search if provided
      if (emailSearch !== null && emailSearch !== '') {
        requestBody.email_search = emailSearch.trim();
      }
      
      console.log('Request body:', requestBody);
      
      const response = await fetch(server_URL + '/api/members', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch contacts');
      }

      const data = await response.json();
      console.log('Received contacts data:', data);
      
      const membersData = Array.isArray(data) ? data : data.data || [];
      
      // Update pagination state
      if (data.pagination) {
        setCurrentPage(data.pagination.current_page);
        setTotalPages(data.pagination.total_pages);
        setTotalRecords(data.pagination.total_records);
      }
      
      // Identify duplicate emails within the same customer_id
      // Use md5_email for duplicate detection (member.email is md5_email if available)
      const emailCustomerCountMap = {};
      membersData.forEach(member => {
        // Use md5_email if available, otherwise use email
        const emailKey = member.md5_email || member.email;
        const key = `${emailKey}_${member.customer_id}`;
        emailCustomerCountMap[key] = (emailCustomerCountMap[key] || 0) + 1;
      });
      
      // Mark members with duplicate emails (same md5_email/email AND same customer_id)
      const membersWithDuplicates = membersData.map(member => {
        const emailKey = member.md5_email || member.email;
        const key = `${emailKey}_${member.customer_id}`;
        return {
          ...member,
          isDuplicateEmail: emailCustomerCountMap[key] > 1
        };
      });
      
      setMembers(membersWithDuplicates);
      
      if (membersData.length === 0) {
        console.log('No contacts found matching the filter criteria');
      } else {
        console.log(`Found ${membersData.length} member(s) on page ${page}`);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setMembers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    fetchMembers(value);
  };
  
  const handleClearFilters = () => {
    setCustomerFilter('');
    setTagFilter('');
    setUnsubscribedFilter('all');
    setEmailSearch('');
    setDuplicateFilter('all');
    setCurrentPage(1); // Reset to page 1 when clearing filters
  };

  const handleProfile = async (customerUserId) => {
    setLoadingProfile(true);
    setProfileOpen(true);
    try {
      // Fetch profile data
      const response = await fetch(server_URL + '/api/members', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'get_profile',
          customer_user_id: customerUserId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setProfileData(data.data);
        // Set selected tags
        const currentTagIds = data.data.tags ? data.data.tags.map(tag => tag.id) : [];
        setSelectedTagIds(currentTagIds);
        // Set note
        setProfileNote(data.data.note || '');
        
        // Fetch available tags for this customer
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const tagsResponse = await fetch(server_URL + '/api/tags', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            method: 'list',
            user_id: userData.id || null,
            role_id: userData.role_id || null,
            customer_id: data.data.customer_id,
          }),
        });

        if (tagsResponse.ok) {
          const tagsData = await tagsResponse.json();
          if (tagsData.status === '1' && tagsData.data) {
            setAvailableTags(tagsData.data || []);
          }
        }
        
        // Fetch report data
        fetchReportData(customerUserId);
        // Fetch email log
        fetchActivityLog(customerUserId);
        // Fetch user activity log (email activity)
        fetchEmailActivityLog(data.data);
      } else {
        alert(data.error || 'Failed to load profile');
        setProfileOpen(false);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      alert('Failed to load profile');
      setProfileOpen(false);
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchCustomFieldsForMigration = async (customerId) => {
    if (!customerId || customerId === 0) {
      setMigrationCustomFields([]);
      return;
    }
    
    try {
      const response = await fetch(server_URL + '/api/trigger_automations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'get_custom_fields',
          customer_id: customerId,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setMigrationCustomFields(data.data || []);
      } else {
        setMigrationCustomFields([]);
      }
    } catch (error) {
      console.error('Error fetching custom fields for migration:', error);
      setMigrationCustomFields([]);
    }
  };

  const handleOpenMigration = async (email, customerId) => {
    try {
      // Fetch custom fields for the customer first
      await fetchCustomFieldsForMigration(customerId);
      
      // Fetch all duplicate records for this email and customer
      const response = await fetch(server_URL + '/api/members', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'get_duplicates',
          email: email,
          customer_id: customerId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch duplicate records');
      }

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setDuplicateRecords(data.data);
        
        // Initialize migration data with the first record's values
        if (data.data.length > 0) {
          const firstRecord = data.data[0];
          // Use original email from the record, or fallback to the passed email
          // The passed email is md5_email, but we need the original email for the migration
          const originalEmail = firstRecord.email || email;
          setMigrationData({
            email: originalEmail,  // Use original email from record
            customer_id: firstRecord.customer_id || customerId,
            first_name: firstRecord.first_name || '',
            last_name: firstRecord.last_name || '',
            cust1: firstRecord.cust1 || '',
            cust2: firstRecord.cust2 || '',
            cust3: firstRecord.cust3 || '',
            cust4: firstRecord.cust4 || '',
            cust5: firstRecord.cust5 || '',
            cust6: firstRecord.cust6 || '',
            note: firstRecord.note || ''
          });
        } else {
          showToast('No duplicate records found', 'warning');
          return;
        }
        
        setMigrationOpen(true);
      } else {
        showToast(data.error || 'Failed to fetch duplicate records', 'error');
      }
    } catch (error) {
      console.error('Error fetching duplicate records:', error);
      showToast('Failed to fetch duplicate records', 'error');
    }
  };

  const handleCloseMigration = () => {
    setMigrationOpen(false);
    setDuplicateRecords([]);
    setMigrationCustomFields([]);
    setMigrationData({
      email: '',
      customer_id: '',
      first_name: '',
      last_name: '',
      cust1: '',
      cust2: '',
      cust3: '',
      cust4: '',
      cust5: '',
      cust6: '',
      note: ''
    });
  };

  const handleMigrationFieldChange = (field, value) => {
    setMigrationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRemoveDuplicate = async (customerUserId, email, customerId) => {
    if (!window.confirm('Are you sure you want to remove this duplicate record? This action cannot be undone.')) {
      return;
    }

    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/members', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'remove_duplicate',
          customer_user_id: customerUserId,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });

      const data = await response.json();
      
      if (data.status === '1') {
        showToast('Duplicate record removed successfully', 'success');
        // Refresh the members list to reflect the changes
        await fetchMembers(currentPage);
      } else {
        showToast(data.error || 'Failed to remove duplicate record', 'error');
      }
    } catch (error) {
      console.error('Error removing duplicate record:', error);
      showToast('Failed to remove duplicate record', 'error');
    }
  };

  const handleMigrate = async () => {
    if (!migrationData.email || !migrationData.customer_id) {
      showToast('Email and Customer ID are required', 'error');
      return;
    }

    setIsMigrating(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/members', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'migrate_duplicates',
          email: migrationData.email,
          customer_id: migrationData.customer_id,
          first_name: migrationData.first_name,
          last_name: migrationData.last_name,
          cust1: migrationData.cust1,
          cust2: migrationData.cust2,
          cust3: migrationData.cust3,
          cust4: migrationData.cust4,
          cust5: migrationData.cust5,
          cust6: migrationData.cust6,
          note: migrationData.note,
          duplicate_ids: duplicateRecords.map(r => r.id),
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });

      const data = await response.json();
      
      if (data.status === '1') {
        showToast('Duplicate records merged successfully', 'success');
        handleCloseMigration();
        // Refresh the members list
        await fetchMembers(currentPage);
      } else {
        showToast(data.error || 'Failed to merge duplicate records', 'error');
      }
    } catch (error) {
      console.error('Error migrating duplicates:', error);
      showToast('Failed to merge duplicate records', 'error');
    } finally {
      setIsMigrating(false);
    }
  };

  const fetchReportData = async (customerUserId) => {
    setLoadingReport(true);
    try {
      const response = await fetch(server_URL + '/api/members', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'get_report',
          customer_user_id: customerUserId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch report');
      }

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setReportData(data.data);
      } else {
        setReportData(null);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      setReportData(null);
    } finally {
      setLoadingReport(false);
    }
  };

  const fetchActivityLog = async (customerUserId) => {
    setLoadingActivityLog(true);
    try {
      const response = await fetch(server_URL + '/api/members', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'get_activity_log',
          customer_user_id: customerUserId,
        }),
      });

      const data = await response.json();
      
      if (data.status === '1' && data.data) {
        setActivityLogs(data.data || []);
      } else {
        setActivityLogs([]);
      }
    } catch (error) {
      console.error('Error fetching email log:', error);
      setActivityLogs([]);
    } finally {
      setLoadingActivityLog(false);
    }
  };

  const fetchEmailActivityLog = async (customerUser) => {
    // Use md5_email if available, otherwise use email field
    const md5Email = customerUser?.md5_email || customerUser?.email;
    
    if (!customerUser || !md5Email || !customerUser.customer_id) {
      setEmailActivityLogs([]);
      setLoadingEmailActivityLog(false);
      return;
    }

    setLoadingEmailActivityLog(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/email_activity_logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'list',
          user_role: userData.role_id,
          user_id: userData.id,
          customer_id: customerUser.customer_id,
          md5_email: md5Email,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setEmailActivityLogs(data.data || []);
      } else {
        console.error('Failed to fetch email activity logs:', data.error || 'Unknown error');
        setEmailActivityLogs([]);
      }
    } catch (error) {
      console.error('Error fetching user activity log:', error);
      setEmailActivityLogs([]);
    } finally {
      setLoadingEmailActivityLog(false);
    }
  };

  const handleCloseProfile = () => {
    setProfileOpen(false);
    setProfileData(null);
    setSelectedTagIds([]);
    setProfileNote('');
    setAvailableTags([]);
    setIsEditingNote(false);
    setTagsListOpen(false);
    setReportData(null);
    setActivityLogs([]);
    setEmailActivityLogs([]);
    setActiveLogTab('email-log');
  };

  const handleSaveProfile = async () => {
    if (!profileData) return;
    
    setIsSavingProfile(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/members', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'update_profile',
          customer_user_id: profileData.id,
          note: profileNote,
          tag_ids: selectedTagIds,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const data = await response.json();
      if (data.status === '1') {
        // Refresh profile data
        await handleProfile(profileData.id);
        showToast('Profile updated successfully', 'success');
        setIsEditingNote(false); // Exit edit mode after successful save
      } else {
        showToast(data.error || 'Failed to update profile', 'error');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      showToast('Failed to update profile', 'error');
    } finally {
      setIsSavingProfile(false);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1>Contacts</h1>
      </div>
      
      {/* Filters */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        alignItems: 'center', 
        mb: 3,
        p: 2,
        bgcolor: '#f5f5f5',
        borderRadius: 1,
        flexWrap: 'wrap'
      }}>
        {/* Email Search */}
        <TextField
          label="Search Email"
          value={emailSearch}
          onChange={(e) => setEmailSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
          placeholder="Enter email address"
        />

        {/* Customer Filter - Only show for role 1 (admin) */}
        {userRole === 1 && (
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Customer</InputLabel>
            <Select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              label="Filter by Customer"
              size="small"
            >
              <MenuItem value="">All Customers</MenuItem>
              {customers.filter(customer => customer.id !== 0).map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.username} ({customer.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Tag Filter */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Tag</InputLabel>
          <Select
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            label="Filter by Tag"
            size="small"
          >
            <MenuItem value="">All Tags</MenuItem>
            {tags.map((tag) => (
              <MenuItem key={tag.id} value={tag.id}>
                {tag.tag}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Unsubscribed/Subscribed Filter */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={unsubscribedFilter}
            onChange={(e) => setUnsubscribedFilter(e.target.value)}
            label="Status"
            size="small"
          >
            <MenuItem value="all">All</MenuItem> 
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
          </Select>
        </FormControl>

        {/* Duplicate Filter */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Display</InputLabel>
          <Select
            value={duplicateFilter}
            onChange={(e) => setDuplicateFilter(e.target.value)}
            label="Duplicates"
            size="small"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="duplicates">Duplicates</MenuItem>
          </Select>
        </FormControl>

        {(customerFilter !== '' || tagFilter !== '' || unsubscribedFilter !== 'all' || emailSearch !== '' || duplicateFilter !== 'all') && (
          <Button 
            variant="outlined"
            color="warning"
            onClick={handleClearFilters}
            sx={{ height: '40px' }}
          >
            Clear Filters
          </Button>
        )}
        
        <Button 
          variant="outlined"
          onClick={fetchMembers}
          sx={{ height: '40px' }}
        >
          Refresh
        </Button>
      </Box>
      
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading contacts...</p>
          </div>
        </div>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Full Name</TableCell>
                    {userRole === 1 && <TableCell>Customer</TableCell>}
                    <TableCell>Tags</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    // Filter members based on duplicateFilter
                    const filteredMembers = duplicateFilter === 'duplicates'
                      ? members.filter(member => member.isDuplicateEmail)
                      : members;
                    
                    if (filteredMembers.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={userRole === 1 ? 7 : 6} align="center" sx={{ py: 5 }}>
                            <Typography variant="h6" color="text.secondary">
                              {duplicateFilter === 'duplicates' ? 'No duplicate records found' : 'No contacts found'}
                            </Typography>
                            {(customerFilter !== '' || tagFilter !== '' || unsubscribedFilter !== 'all' || emailSearch !== '' || duplicateFilter !== 'all') && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Try clearing the filters or selecting different options
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    return filteredMembers.map((member) => {
                      // Build full name from first_name and last_name
                      const fullName = [member.first_name, member.last_name]
                        .filter(Boolean)
                        .join(' ')
                        .trim() || 'N/A';
                      
                      return (
                        <TableRow 
                          key={member.id} 
                          hover
                          sx={{
                            bgcolor: member.isDuplicateEmail ? '#fff3cd' : 'inherit' // Light yellow background for duplicates
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                {member.email}
                              </Typography>
                              {member.isDuplicateEmail && (
                                <Chip 
                                  label="Duplicate" 
                                  size="small" 
                                  color="warning" 
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: '20px' }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {fullName}
                            </Typography>
                          </TableCell>
                          {userRole === 1 && (
                            <TableCell>
                              {member.customer_username || 'N/A'}
                              {member.customer_email && (
                                <Typography variant="caption" display="block" sx={{ color: '#666', fontSize: '0.75rem' }}>
                                  {member.customer_email}
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            {member.tags && member.tags.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {member.tags.map((tag) => (
                                  <Chip
                                    key={tag.id}
                                    label={tag.tag}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No tags
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {member.unsubscribed === 1 ? (
                              <Chip
                                label="Unsubscribed"
                                size="small"
                                color="error"
                              />
                            ) : (
                              <Chip
                                label="Active"
                                size="small"
                                color="success"
                              />
                            )}
                            {member.unsubscribed_at && (
                              <Typography variant="caption" display="block" sx={{ color: '#666', fontSize: '0.75rem', mt: 0.5 }}>
                                {new Date(member.unsubscribed_at).toLocaleString()}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {member.create_date ? new Date(member.create_date).toLocaleString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              onClick={() => handleProfile(member.id)}
                              sx={{ mr: 1 }}
                            >
                              Profile
                            </Button>
                            {member.isDuplicateEmail && (
                              <>
                                <Button
                                  variant="outlined"
                                  color="warning"
                                  size="small"
                                  onClick={() => {
                                    // Use md5_email if available, otherwise use email
                                    const emailToSearch = member.md5_email || member.email;
                                    handleOpenMigration(emailToSearch, member.customer_id);
                                  }}
                                  sx={{ mr: 1 }}
                                >
                                  Merge
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={() => {
                                    // Use md5_email if available, otherwise use email
                                    const emailToSearch = member.md5_email || member.email;
                                    handleRemoveDuplicate(member.id, emailToSearch, member.customer_id);
                                  }}
                                >
                                  Remove
                                </Button>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Pagination Controls - Outside loading check */}
      {!isLoading && (
        <>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing page {currentPage} of {totalPages} | Total Records: {totalRecords}
              </Typography>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
          
          {totalPages === 1 && totalRecords > 0 && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                Total Records: {totalRecords}
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Profile Modal */}
      <Modal
        open={profileOpen}
        onClose={handleCloseProfile}
        aria-labelledby="profile-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 1400,
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
        }}>
          {loadingProfile ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <Typography sx={{ ml: 2 }}>Loading profile...</Typography>
            </Box>
          ) : profileData ? (
            <Box>
              <Typography id="profile-modal-title" variant="h5" component="h2" mb={2} sx={{ fontWeight: 'bold' }}>
                Profile
              </Typography>
              
              <Grid container spacing={2}>
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Basic Information</Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{profileData.email}</Typography>
                    </Grid>
                    {(profileData.first_name || profileData.last_name) && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Full Name</Typography>
                        <Typography variant="body1">
                          {[profileData.first_name, profileData.last_name].filter(Boolean).join(' ') || 'N/A'}
                        </Typography>
                      </Grid>
                    )}
                    {userRole === 1 && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Customer</Typography>
                        <Typography variant="body1">
                          {profileData.customer_username || 'N/A'}
                        </Typography>
                        {profileData.customer_email && (
                          <Typography variant="caption" color="text.secondary">
                            {profileData.customer_email}
                          </Typography>
                        )}
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        {profileData.unsubscribed === 1 ? (
                          <>
                            <Chip label="Unsubscribed" color="error" size="small" />
                            {profileData.unsubscribed_at && (
                              <Typography variant="caption" color="text.secondary">
                                {new Date(profileData.unsubscribed_at).toLocaleString()}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Chip label="Active" color="success" size="small" />
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Created Date</Typography>
                      <Typography variant="body1">
                        {profileData.create_date ? new Date(profileData.create_date).toLocaleString() : 'N/A'}
                      </Typography>
                    </Grid>
                    
                    {/* Custom Fields - moved inside Basic Information */}
                    {(() => {
                      // Function to format date values
                      const formatCustomFieldValue = (value) => {
                        if (!value) return value;
                        
                        const valueStr = String(value).trim();
                        
                        // Try to parse as date
                        const date = new Date(valueStr);
                        
                        // Check if it's a valid date
                        if (!isNaN(date.getTime())) {
                          // Check if time is 00:00:00 (midnight)
                          const hours = date.getHours();
                          const minutes = date.getMinutes();
                          const seconds = date.getSeconds();
                          
                          if (hours === 0 && minutes === 0 && seconds === 0) {
                            // Format as DD/MM/YYYY
                            const day = String(date.getDate()).padStart(2, '0');
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const year = date.getFullYear();
                            return `${day}/${month}/${year}`;
                          }
                        }
                        
                        // Return original value if not a date or has time
                        return valueStr;
                      };
                      
                      // Build custom fields data with labels from config
                      const customFieldsData = [];
                      const customFieldsConfig = profileData.custom_fields_config || [];
                      
                      console.log('[DEBUG] Profile data:', profileData);
                      console.log('[DEBUG] Custom fields config:', customFieldsConfig);
                      console.log('[DEBUG] Custom field values:', {
                        cust1: profileData.cust1,
                        cust2: profileData.cust2,
                        cust3: profileData.cust3,
                        cust4: profileData.cust4,
                        cust5: profileData.cust5,
                        cust6: profileData.cust6
                      });
                      
                      // Create a map of tag to name for quick lookup
                      const tagToNameMap = {};
                      if (Array.isArray(customFieldsConfig)) {
                        customFieldsConfig.forEach(cf => {
                          if (cf && cf.tag && cf.name) {
                            tagToNameMap[cf.tag] = cf.name;
                          }
                        });
                      }
                      
                      console.log('[DEBUG] Tag to name map:', tagToNameMap);
                      
                      // Check each cust field and add to display if it has a value
                      const custFields = [
                        { tag: 'cust1', value: profileData.cust1 },
                        { tag: 'cust2', value: profileData.cust2 },
                        { tag: 'cust3', value: profileData.cust3 },
                        { tag: 'cust4', value: profileData.cust4 },
                        { tag: 'cust5', value: profileData.cust5 },
                        { tag: 'cust6', value: profileData.cust6 }
                      ];
                      
                      custFields.forEach(cf => {
                        // Check if value exists and is not empty string or null/undefined
                        if (cf.value !== null && cf.value !== undefined && String(cf.value).trim() !== '') {
                          // Format the value (handles date formatting)
                          const formattedValue = formatCustomFieldValue(cf.value);
                          
                          customFieldsData.push({
                            tag: cf.tag,
                            label: tagToNameMap[cf.tag] || `Custom Field ${cf.tag.replace('cust', '')}`,
                            value: formattedValue
                          });
                        }
                      });
                      
                      console.log('[DEBUG] Custom fields data to display:', customFieldsData);
                      
                      // Return custom fields as Grid items
                      return customFieldsData.map((cf, index) => (
                        <Grid key={index} item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">{cf.label}</Typography>
                          <Typography variant="body1">{cf.value}</Typography>
                        </Grid>
                      ));
                    })()}
                  </Grid>
                </Grid>

                {/* Note */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6" sx={{ color: 'primary.main' }}>Note</Typography>
                    {!isEditingNote && (
                      <IconButton
                        size="small"
                        onClick={() => setIsEditingNote(true)}
                        sx={{ ml: 1 }}
                      >
                        <FaEdit />
                      </IconButton>
                    )}
                  </Box>
                  <Divider sx={{ mb: 1.5 }} />
                  {isEditingNote ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={profileNote}
                      onChange={(e) => setProfileNote(e.target.value)}
                      placeholder="Add a note..."
                      variant="outlined"
                      size="small"
                    />
                  ) : (
                    <Typography variant="body1" sx={{ minHeight: '60px', py: 1 }}>
                      {profileNote || <span style={{ color: '#999' }}>No note</span>}
                    </Typography>
                  )}
                </Grid>

                {/* Tags */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6" sx={{ color: 'primary.main' }}>
                      Tags
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setTagsListOpen(!tagsListOpen)}
                    >
                      {tagsListOpen ? 'Hide Tags' : 'Show Available Tags'}
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 1.5 }} />
                  
                  {/* Show assigned tags by default */}
                  {selectedTagIds.length > 0 && (
                    <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {availableTags
                        .filter(tag => selectedTagIds.includes(tag.id))
                        .map((tag) => (
                          <Chip
                            key={tag.id}
                            label={tag.tag}
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        ))}
                    </Box>
                  )}
                  
                  {/* Collapsible tags list */}
                  <Collapse in={tagsListOpen}>
                    {availableTags.length > 0 ? (
                      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, maxHeight: '200px', overflow: 'auto', mt: 1 }}>
                        <List dense>
                          {availableTags.map((tag) => (
                            <ListItem key={tag.id} disablePadding>
                              <ListItemButton onClick={() => {
                                const isSelected = selectedTagIds.includes(tag.id);
                                if (isSelected) {
                                  setSelectedTagIds(selectedTagIds.filter(id => id !== tag.id));
                                } else {
                                  setSelectedTagIds([...selectedTagIds, tag.id]);
                                }
                              }}>
                                <Checkbox
                                  checked={selectedTagIds.includes(tag.id)}
                                  edge="start"
                                />
                                <ListItemText primary={tag.tag} />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        No tags available for this customer
                      </Typography>
                    )}
                  </Collapse>
                  
                  {selectedTagIds.length === 0 && !tagsListOpen && (
                    <Typography variant="body2" color="text.secondary">
                      No tags assigned. Click "Show Tags" to add tags.
                    </Typography>
                  )}
                </Grid>

                {/* Report Section */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Report</Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  {loadingReport ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                      <CircularProgress size={20} sx={{ mr: 2 }} />
                      <Typography variant="body2">Loading report...</Typography>
                    </Box>
                  ) : reportData ? (
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Total Email Sends
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {reportData.total_sent || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 1, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Opens
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {reportData.opens || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Not Open Emails
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                            {reportData.not_open || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Link Clicks
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                            {reportData.link_clicks || 0}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No report data available
                    </Typography>
                  )}
                </Grid>

                {/* Logs Section with tabs */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Logs</Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Button
                      size="small"
                      variant={activeLogTab === 'email-log' ? 'contained' : 'outlined'}
                      onClick={() => setActiveLogTab('email-log')}
                    >
                      Email Log
                    </Button>
                    <Button
                      size="small"
                      variant={activeLogTab === 'user-activity' ? 'contained' : 'outlined'}
                      onClick={() => setActiveLogTab('user-activity')}
                    >
                      User Activity
                    </Button>
                  </Stack>
                  <Divider sx={{ mb: 1.5 }} />

                  {activeLogTab === 'email-log' ? (
                    loadingActivityLog ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                        <CircularProgress size={20} sx={{ mr: 2 }} />
                        <Typography variant="body2">Loading email log...</Typography>
                      </Box>
                    ) : activityLogs.length > 0 ? (
                      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Campaign Name</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Open Count</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {activityLogs.map((log) => (
                              <TableRow key={log.id} hover>
                                <TableCell>
                                  {log.date_time ? new Date(log.date_time).toLocaleString() : 'N/A'}
                                </TableCell>
                                <TableCell>{log.campaign_name}</TableCell>
                                <TableCell>
                                  {log.opened > 0 ? (
                                    <Chip label="Opened" color="success" size="small" />
                                  ) : (
                                    <Chip label="Not Opened" color="default" size="small" />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {log.opened || 0}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No email log available
                      </Typography>
                    )
                  ) : null}

                  {activeLogTab === 'user-activity' ? (
                    loadingEmailActivityLog ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                        <CircularProgress size={20} sx={{ mr: 2 }} />
                        <Typography variant="body2">Loading user activity...</Typography>
                      </Box>
                    ) : emailActivityLogs.length > 0 ? (
                      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Event Type</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Message</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {emailActivityLogs.map((log) => (
                              <TableRow key={log.id} hover>
                                <TableCell>
                                  {log.created_at ? new Date(log.created_at).toLocaleString() : 'N/A'}
                                </TableCell>
                                <TableCell>{log.event_type || 'N/A'}</TableCell>
                                <TableCell>
                                  <Typography variant="body2">{log.message}</Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No user activity available
                      </Typography>
                    )
                  ) : null}
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button variant="outlined" onClick={handleCloseProfile} disabled={isSavingProfile}>
                  Close
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleSaveProfile}
                  disabled={isSavingProfile}
                >
                  {isSavingProfile ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Modal>

      {/* Toast Notification */}
      <Snackbar
        open={toastState.open}
        autoHideDuration={6000}
        onClose={() => setToastState(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToastState(prev => ({ ...prev, open: false }))}
          severity={toastState.severity}
          sx={{ width: '100%' }}
        >
          {toastState.message}
        </Alert>
      </Snackbar>

      {/* Migration Modal */}
      <Modal
        open={migrationOpen}
        onClose={handleCloseMigration}
        aria-labelledby="migration-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 800,
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <Typography id="migration-modal-title" variant="h6" component="h2" mb={3}>
          Merge Duplicate Records
          </Typography>
          
          {duplicateRecords.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Found {duplicateRecords.length} duplicate record(s). Select values for the merged record:
              </Typography>
              
              <Grid container spacing={2}>
                {/* First Name */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>First Name</InputLabel>
                    <Select
                      value={migrationData.first_name || ''}
                      onChange={(e) => handleMigrationFieldChange('first_name', e.target.value)}
                      label="First Name"
                    >
                      <MenuItem value="">-- Select --</MenuItem>
                      {duplicateRecords.map((record, index) => (
                        <MenuItem key={index} value={record.first_name || ''}>
                          {record.first_name || '(empty)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Last Name */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Last Name</InputLabel>
                    <Select
                      value={migrationData.last_name || ''}
                      onChange={(e) => handleMigrationFieldChange('last_name', e.target.value)}
                      label="Last Name"
                    >
                      <MenuItem value="">-- Select --</MenuItem>
                      {duplicateRecords.map((record, index) => (
                        <MenuItem key={index} value={record.last_name || ''}>
                          {record.last_name || '(empty)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Custom Fields - Only show if custom fields are defined */}
                {migrationCustomFields.length > 0 && [1, 2, 3, 4, 5, 6].map(num => {
                  // Find custom field with matching tag (e.g., "cust1", "cust2", etc.)
                  const customField = migrationCustomFields.find(cf => cf.tag === `cust${num}`);
                  
                  // Only render if custom field exists
                  if (!customField) {
                    return null;
                  }
                  
                  return (
                    <Grid item xs={12} sm={6} key={num}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{customField.name}</InputLabel>
                        <Select
                          value={migrationData[`cust${num}`] || ''}
                          onChange={(e) => handleMigrationFieldChange(`cust${num}`, e.target.value)}
                          label={customField.name}
                        >
                          <MenuItem value="">-- Select --</MenuItem>
                          {duplicateRecords.map((record, index) => (
                            <MenuItem key={index} value={record[`cust${num}`] || ''}>
                              {record[`cust${num}`] || '(empty)'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  );
                })}
                
                {/* Note */}
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Note</InputLabel>
                    <Select
                      value={migrationData.note || ''}
                      onChange={(e) => handleMigrationFieldChange('note', e.target.value)}
                      label="Note"
                    >
                      <MenuItem value="">-- Select --</MenuItem>
                      {duplicateRecords.map((record, index) => (
                        <MenuItem key={index} value={record.note || ''}>
                          {record.note || '(empty)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Duplicate Records List */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Duplicate Records to be Merged:</Typography>
                  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, maxHeight: '200px', overflow: 'auto' }}>
                    {duplicateRecords.map((record, index) => {
                      // Create a map of custom field tags to names
                      const customFieldMap = {};
                      migrationCustomFields.forEach(cf => {
                        if (cf && cf.tag && cf.name) {
                          customFieldMap[cf.tag] = cf.name;
                        }
                      });
                      
                      // Build custom fields display array
                      const customFieldsDisplay = [];
                      [1, 2, 3, 4, 5, 6].forEach(num => {
                        const tag = `cust${num}`;
                        if (customFieldMap[tag]) {
                          customFieldsDisplay.push({
                            label: customFieldMap[tag],
                            value: record[tag] || '(empty)'
                          });
                        }
                      });
                      
                      return (
                        <Box key={record.id} sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            Record {index + 1} (ID: {record.id})
                          </Typography>
                          <Typography variant="caption" display="block">
                            First Name: {record.first_name || '(empty)'} | Last Name: {record.last_name || '(empty)'}
                          </Typography>
                          {customFieldsDisplay.length > 0 && (
                            <Typography variant="caption" display="block">
                              {customFieldsDisplay.map((cf, idx) => (
                                <span key={idx}>
                                  {cf.label}: {cf.value}
                                  {idx < customFieldsDisplay.length - 1 ? ' | ' : ''}
                                </span>
                              ))}
                            </Typography>
                          )}
                          {record.note && (
                            <Typography variant="caption" display="block">
                              Note: {record.note}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Grid>
              </Grid>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleCloseMigration}
                  disabled={isMigrating}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleMigrate}
                  disabled={isMigrating}
                >
                  {isMigrating ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Merging...
                    </>
                  ) : (
                    'Merge'
                  )}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default Members;

